import React from 'react'

const baseConstants = {
  movingAverageRange: 90,
  titleTag: '| Simplesat',
  simplesatTokenName: 'simplesatToken',
  simplesatWorkspaceTokenName: 'simplesatWorkspaceToken',
  simplesatWorkspaceTimezone: 'simplesatWorkspaceTimezone',
  simplesatWorkspaceWeekStartDay: 'simplesatWorkspaceWeekStartDay',
  emailValidationRegEx: '[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,10}$',
  inactiveSubscriptionStatus: ['unpaid', 'past_due', 'canceled', 'incomplete_expired'],
  activeSubscriptionStatus: ['active', 'trialing'],
  permissionDeniedErrorMessage:
    "You don't have the necessary permissions to access this feature. Please ask one of your teammates to give you account permission.",
  surveyNotFoundErrorMessage:
    'This survey could not be found. It may have been deleted or archived.',
  surveyViewNotFoundErrorMessage:
    'This survey view could not be found. The survey may have been deleted or archived, or this view was removed.',
  permissions: {
    canAddSurvey: 'can_add_survey',
    canChangeAccountSettings: 'can_change_account_settings',
    canChangeSurvey: 'can_change_survey',
    canDeleteFeedback: 'can_delete_feedback',
    canExportFeedback: 'can_export_feedback',
    canInviteUser: 'can_invite_user',
    canPublishedFeedback: 'can_published_feedback',
    canViewAccountKey: 'can_view_account_key',
    canViewBillingPage: 'can_view_billing_page',
    canViewIntegrationPage: 'can_view_integration_page',
    canViewTestimonialPage: 'can_view_testimonial_page',
    useNewSurveyBuilder: 'use_new_survey_builder',
    canImportCustomersCSV: 'can_import_customers_csv',
    accessApiKey: 'access_api_key',
    accessAuditLogs: 'access_audit_logs',
    deleteResponses: 'delete_responses',
    editRatingsOnResponses: 'edit_ratings_on_responses',
    editTagsOnResponses: 'edit_tags_on_responses',
    editTeamMembersOnResponses: 'edit_team_members_on_responses',
    editTopicsOnResponses: 'edit_topics_on_responses',
    exportCustomers: 'export_customers',
    importFeedback: 'import_feedback',
    publishFeedback: 'publish_feedback',
    manageAccountSettings: 'manage_account_settings',
    manageAuthenticationSecurity: 'manage_authentication_security',
    manageBillingInformation: 'manage_billing_information',
    manageBrands: 'manage_brands',
    manageCustomers: 'manage_customers',
    manageCustomDashboards: 'manage_custom_dashboards',
    manageDashboardViews: 'manage_dashboard_views',
    manageEmailAuthentication: 'manage_email_authentication',
    manageFeedbackScheduledReports: 'manage_feedback_scheduled_reports',
    manageFeedbackViews: 'manage_feedback_views',
    manageFeedbackWidgets: 'manage_feedback_widgets',
    manageIntegrations: 'manage_integrations',
    manageReportsScheduledReports: 'manage_reports_scheduled_reports',
    manageReportsViews: 'manage_reports_views',
    manageRoles: 'manage_roles',
    manageSurveys: 'manage_surveys',
    manageTopicSettings: 'manage_topic_settings',
    manageUsers: 'manage_users',
    manageUserAccessToSurveys: 'manage_user_access_to_surveys',
    viewBillingPage: 'view_billing_page',
    viewCustomers: 'view_customers',
    viewCustomDashboards: 'view_custom_dashboards',
    viewDashboards: 'view_dashboards',
    viewFeedback: 'view_feedback',
    viewProfileNotification: 'view_profile_notification',
    viewProfileSummary: 'view_profile_summary',
    viewIntegrations: 'view_integrations',
    viewOnboarding: 'view_onboarding_page',
    viewReports: 'view_reports',
    viewSuccessDashboard: 'view_success_dashboard',
    viewSurveysPage: 'view_surveys_page',
    viewSurveyDashboards: 'view_survey_dashboards',
    viewSettings: 'view_settings',
    viewTeamMembers: 'view_team_members',
    manageTeamMembers: 'manage_team_members',
  },
  oauth: {
    noncePrefix: 'simplesat.oauth',
  },
  questionType: {
    comment: 'comment',
    slider: 'slider',
    multipleChoice: 'multipleChoice',
    rating_scale_minmax: 'rating_scale_minmax',
    multi_select_choice: 'multi_select_choice',
    star: 'star',
    file_upload: 'file_upload',
  },
  questionMetricType: {
    csat: 'CSAT',
    nps: 'NPS',
    custom: 'CUSTOM',
    ces: 'CES',
    star: '5-STAR',
  },
  questionMetricTitle: {
    CSAT: 'CSAT (Customer Satisfaction Score)',
    NPS: 'NPS (Net Promoter Score®)',
    CES: 'CES 2.0 (Customer Effort Score)',
    star: '5-Star',
  },
  DEFAULT_CSAT_LABEL_STATE: [
    {
      label: 'Terribly disappointing',
      value: '1',
      sentiment: 'negative',
      order: 1,
    },
    {
      label: "Bad, I'm unsatisfied",
      value: '2',
      sentiment: 'negative',
      order: 2,
    },
    {
      label: 'Okay, but not great',
      value: '3',
      sentiment: 'neutral',
      order: 3,
    },
    {
      label: "Great, I'm satisfied",
      value: '4',
      sentiment: 'positive',
      order: 4,
    },
    {
      label: 'Absolutely amazing!',
      value: '5',
      sentiment: 'positive',
      order: 5,
    },
  ],
  SCALE_RATING: {
    2: [2, 4],
    3: [2, 3, 4],
    4: [2, 3, 4, 5],
    5: [1, 2, 3, 4, 5],
  },
  emailSurveyTypes: ['ongoing_email', 'oneoff_email', 'NPS', 'event_based_email'],
  npsScoreColor: [
    '#D0001C',
    '#D0001C',
    '#D0001C',
    '#D0001C',
    '#D0001C',
    '#D0001C',
    '#D0001C',
    '#F5A623',
    '#F5A623',
    '#4EC677',
    '#4EC677',
  ],
  // eslint-disable-next-line
  EMAIL_REGEX:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  surveyHost: 'http://localhost:3000',
  surveyConfigurationsUrl:
    process.env.REACT_APP_SURVEY_CONFIGURATIONS_URL ||
    'https://cdn-dev.simplesat.io/config/surveyConfigurations.json',
  competitorCsvConfigurationsUrl:
    process.env.REACT_APP_COMPETITOR_CSV_CONFIGURATIONS_URL ||
    'https://cdn-dev.simplesat.io/config/competitorCsvConfigurations.json',
  WIDGET_FONT_OPTIONS: [
    { key: 'lato', text: 'Lato', value: 'lato' },
    { key: 'helvetica_neue', text: 'Helvetica Neue', value: 'helvetica_neue' },
    { key: 'roboto', text: 'Roboto', value: 'roboto' },
    { key: 'open_sans', text: 'Open Sans', value: 'open_sans' },
    { key: 'montserrat', text: 'Montserrat', value: 'montserrat' },
    { key: 'raleway', text: 'Raleway', value: 'raleway' },
    { key: 'nunito_sans', text: 'Nunito Sans', value: 'nunito_sans' },
    { key: 'inter', text: 'Inter', value: 'inter' },
    { key: 'poppins', text: 'Poppins', value: 'poppins' },
    { key: 'kalam', text: 'Kalam', value: 'kalam' },
    { key: 'amatic_sc', text: 'Amatic SC', value: 'amatic_sc' },
    { key: 'caveat', text: 'Caveat', value: 'caveat' },
    { key: 'special_elite', text: 'Special Elite', value: 'special_elite' },
    { key: 'cormorant', text: 'Cormorant', value: 'cormorant' },
    { key: 'bangers', text: 'Bangers', value: 'bangers' },
    { key: 'carter_one', text: 'Carter One', value: 'carter_one' },
    { key: 'pt_serif', text: 'PT Serif', value: 'pt_serif' },
    { key: 'orbitron', text: 'Orbitron', value: 'orbitron' },
    { key: 'oswald', text: 'Oswald', value: 'oswald' },
  ],
  TEXT_ALIGNMENT_OPTIONS: [
    { key: 'center', text: 'Center', value: 'center' },
    { key: 'left', text: 'Left', value: 'left' },
    { key: 'right', text: 'Right', value: 'right' },
  ],
  SHADOW_OPTIONS: [
    { key: 'heavy', text: 'Heavy', value: 'heavy' },
    { key: 'normal', text: 'Normal', value: 'normal' },
    { key: 'light', text: 'Light', value: 'light' },
    { key: 'flat', text: 'Flat', value: 'flat' },
    { key: 'expand', text: 'Expand', value: 'expand' },
    { key: 'subtle', text: 'Subtle', value: 'subtle' },
    {
      key: 'hidden',
      value: 'hidden',
      text: <HiddenDropdownText />,
    },
  ],
  COMPANY_VISIBILITY_OPTIONS: [
    { key: 'visible', text: 'Show', value: 'visible' },
    {
      key: 'hidden',
      value: 'hidden',
      text: <HiddenDropdownText />,
    },
  ],
  CUSTOMER_NAME_FORMAT_OPTIONS: [
    { key: 'First Last', text: 'First Last', value: 'First Last' },
    { key: 'First L.', text: 'First L.', value: 'First L.' },
    { key: 'FL', text: 'FL', value: 'FL' },
    {
      key: 'hidden',
      value: 'hidden',
      text: <HiddenDropdownText />,
    },
  ],
  FONT_WEIGHT_OPTIONS: [
    { key: '300', text: 'Light', value: '300' },
    { key: '400', text: 'Regular', value: '400' },
    { key: '700', text: 'Bold', value: '700' },
    { key: '900', text: 'Black', value: '900' },
  ],
  FONT_STYLE_OPTIONS: [
    { key: 'normal', text: 'Normal', value: 'normal' },
    { key: 'italic', text: 'Italic', value: 'italic' },
  ],
  DATE_FORMAT_OPTIONS: [
    { key: 'MMM D, YYYY', text: 'Jan 1, 2021', value: 'MMM D, YYYY' },
    { key: 'D MMM, YYYY', text: '1 Jan, 2021', value: 'D MMM, YYYY' },
    { key: 'MMM YYYY', text: 'Jan 2021', value: 'MMM YYYY' },
    { key: 'MMM Do, YYYY', text: 'Jan 1st, 2021', value: 'MMM Do, YYYY' },
    { key: 'MMMM Do, YYYY', text: 'January 1st, 2021', value: 'MMMM Do, YYYY' },
    {
      key: 'hidden',
      value: 'hidden',
      text: <HiddenDropdownText />,
    },
  ],
  DATE_RANGE_OPTIONS: [
    { key: 7, text: '7 days', value: 7 },
    { key: 30, text: '30 days', value: 30 },
    { key: 90, text: '90 days', value: 90 },
    { key: 180, text: '180 days', value: 180 },
    { key: 360, text: '360 days', value: 360 },
  ],
  BACKGROUND_COLOR_OPTIONS: [
    '#D3F8DF',
    '#4EC677',
    '#00C7E6',
    '#66B2FF',
    '#0058B3',
    '#BE9AF5',
    '#FFC86D',
    '#DEDEDE',
  ],
  ACCENT_COLOR_OPTIONS: [
    '#4EC677',
    '#00C7E6',
    '#007EFF',
    '#0058B3',
    '#804DC8',
    '#FF7989',
    '#B10214',
    '#F2A200',
  ],
  CHART_TICK_AMOUNT: 5,
  sentryDSN: process.env.REACT_APP_SENTRY_DSN,
  sentryTracesSampleRate: 0.1,
  homeDashboardIcon: 'https://cdn.simplesat.io/images/home-dashboard-icon.png',
  simplesatIconRoundUrl: 'https://cdn.simplesat.io/logo/simplesat-logo-round.png',
  simplesatIconWhiteUrl: 'https://cdn.simplesat.io/logo/simplesat-logomark-white.png',
  simplesatLogoBlackUrl: 'https://cdn.simplesat.io/logo/simplesat-logo-large.png',
  simplesatLogoBlackUrlSvg: 'https://cdn.simplesat.io/logo/simplesat-logo-medium.svg',
  simplesatLogoGreenUrl: 'https://cdn.simplesat.io/logo/simplesat-logomark-green-medium.png',
  simplesatOpenedSidebarUrl: 'https://cdn.simplesat.io/logo/simplesat-opened-sidebar.svg',
  simplesatClosedSidebarUrl: 'https://cdn.simplesat.io/logo/simplesat-closed-sidebar.svg',
  eventBasedEmailIcon: 'https://cdn.simplesat.io/images/event-based-email-icon.svg',
  externalEmailEmbedIcon: 'https://cdn.simplesat.io/images/external-email-embed-icon.svg',
  webEmbedIcon: 'https://cdn.simplesat.io/images/web-embed-icon.svg',
  directEmailIcon: 'https://cdn.simplesat.io/images/direct-email-icon.svg',
  oneOffEmailIcon: 'https://cdn.simplesat.io/images/one-off-email-icon.svg',
  ongoingEmailIcon: 'https://cdn.simplesat.io/images/on-going-email-icon.svg',
  ssoLoginIcon: 'https://cdn.simplesat.io/images/icon-sso-login.svg',
  googleLoginIcon: 'https://cdn.simplesat.io/images/icon-google.svg',
  microsoftLoginIcon: 'https://cdn.simplesat.io/images/icon-microsoft.svg',
  g2Badge1: 'https://cdn.simplesat.io/images/g2_badge_1.svg',
  g2Badge2: 'https://cdn.simplesat.io/images/g2_badge_2.svg',
  g2Badge3: 'https://cdn.simplesat.io/images/g2_badge_3.svg',
  awardsStar: 'https://cdn.simplesat.io/images/awards-star.png',
  widgetLineChartIcon: 'https://cdn.simplesat.io/images/widgets/line-chart-icon.svg',
  widgetEngagementIcon: 'https://cdn.simplesat.io/images/widgets/engagement-icon.svg',
  widgetPieChartIcon: 'https://cdn.simplesat.io/images/widgets/pie-chart-icon.svg',
  widgetBarChartIcon: 'https://cdn.simplesat.io/images/widgets/bar-chart-icon.svg',
  widgetStackedBarChartIcon: 'https://cdn.simplesat.io/images/widgets/stack-bar-chart-icon.svg',
  widgetScoreIcon: 'https://cdn.simplesat.io/images/widgets/score-icon.svg',
  widgetTableIcon: 'https://cdn.simplesat.io/images/widgets/table-icon.svg',
  widgetLeaderboardIcon: 'https://cdn.simplesat.io/images/widgets/leaderboard-icon.svg',
  iconArrow: 'https://cdn.simplesat.io/images/widgets/arrow-icon.svg',
  iconCheckedBox: 'https://cdn.simplesat.io/images/checklist-check-icon.svg',
  iconUnCheckedBox: 'https://cdn.simplesat.io/images/checklist-uncheck-icon.svg',
  iconPreview90Daywidget: 'https://cdn.simplesat.io/images/widgets/preview-ninety-day-widget-2.png',
  iconPreviewRatingsWidget: 'https://cdn.simplesat.io/images/widgets/preview-ratings-widget-2.png',
  iconPreviewResponsesWidget:
    'https://cdn.simplesat.io/images/widgets/preview-responses-widget-2.png',
  iconPreviewAnswerDistributionWidget:
    'https://cdn.simplesat.io/images/widgets/preview-answer-distribution-2.png',
  iconPreviewLeaderboardWidget:
    'https://cdn.simplesat.io/images/widgets/preview-leaderboard-widget-2.png',
  iconPreviewQuestionCompletion:
    'https://cdn.simplesat.io/images/widgets/preview-question-completion-2.png',
  iconPreviewQuestionCompletionSummary:
    'https://cdn.simplesat.io/images/widgets/preview-question-completion-summary-2.png',
  iconPreviewAnswersSummary:
    'https://cdn.simplesat.io/images/widgets/preview-answers-summary-2.png',
  iconPreviewSatisfactionScore:
    'https://cdn.simplesat.io/images/widgets/preview-satisfaction-score-2.png',
  iconPreviewSentimentDistribution:
    'https://cdn.simplesat.io/images/widgets/preview-sentiment-distribution-2.png',
  iconPreviewTopicTrends:
    'data:image/svg+xml;base64,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',
  iconPreviewTopicFrequency:
    'https://cdn.simplesat.io/images/widgets/preview-topic-frequency-widget-2.png',
  iconPreviewTopicLeaderboard:
    'https://cdn.simplesat.io/images/widgets/preview-topic-leaderboard-widget-2.png',
  feedbackLayoutLineTableIcon: 'https://cdn.simplesat.io/images/line-table.svg',
  feedbackLayoutLineTableActiveIcon: 'https://cdn.simplesat.io/images/line-table-active.svg',
  feedbackLayoutLineListIcon: 'https://cdn.simplesat.io/images/line-list.svg',
  feedbackLayoutLineListActiveIcon: 'https://cdn.simplesat.io/images/line-list-active.svg',
  feedbackLayoutRowIcon: 'https://cdn.simplesat.io/images/icon-row.svg',
  feedbackLayoutRowActiveIcon: 'https://cdn.simplesat.io/images/icon-row-active.svg',
  feedbackLayoutColumnIcon: 'https://cdn.simplesat.io/images/icon-columns.svg',
  feedbackLayoutColumnActiveIcon: 'https://cdn.simplesat.io/images/icon-columns-active.svg',
  questionColumnVisibilityIcon:
    'https://cdn.simplesat.io/images/question-column-visibility-icon.svg',
  onboardingLoadingIcon: 'https://cdn.simplesat.io/images/onboarding-loading.svg',
}

function HiddenDropdownText() {
  return (
    <span>
      <i className="icon fas fa-eye-slash" />
      <span className="ml-1">Hide</span>
    </span>
  )
}

export default baseConstants



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/config/constants.base.js